import { RefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';
import { useHistory } from 'react-router-dom';
import { deleteTenantsFromGroup } from 'Services';
import { RQ_DELETE_TENANTS_FROM_TENANT_GROUP, RQ_TENANT_GROUP_TENANTS, RQ_TENANT_GROUPS } from 'Utils';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { getTranslationErrorHandling } from '../helper';

type RemoveTenantGroupProps = {
    formRef: RefObject<FormikProps<TenantCheckboxTableForm>>;
    tenantGroupId: string;
    tenantGroupName?: string;
};

const useRemoveTenantsFromTenantGroup = ({ tenantGroupId, tenantGroupName, formRef }: RemoveTenantGroupProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const tenantGroupEntryKey = getTranslationErrorHandling(t, 'tenantGroup');
    const { addAlert } = useAlertBanner();
    const { clearAll, addTechnicalErrorModal } = useReduxModal();
    const queryClient = useQueryClient();

    const handleOnSuccess = (variable: { tenantKeys: string[] }) => {
        addAlert({
            id: 'remove-tenants-from-tenantGroup-success',
            content: tenantGroupEntryKey('deleteTenantsSuccessful', {
                count: variable.tenantKeys.length,
                tenantGroupName
            }),
            status: 'success'
        });
        queryClient.invalidateQueries([RQ_TENANT_GROUP_TENANTS, tenantGroupId]);
        // uncheck the header checkbox
        if (formRef.current) {
            formRef.current.setFieldValue('headerCheckboxSelected', false);
        }
    };

    const handleAlreadyDeleted = () => {
        addAlert({
            id: 'remove-tenants-from-tenant-groups-already-deleted-error',
            content: tenantGroupEntryKey('deleteTenantsAlreadyDeleted'),
            status: 'danger'
        });
        queryClient.invalidateQueries([RQ_TENANT_GROUP_TENANTS, tenantGroupId]);
    };

    const handleTenantGroupDeleted = () => {
        addTechnicalErrorModal(
            '',
            t('Tenant Group Unavailable'),
            () => {
                clearAll();
                queryClient.invalidateQueries([RQ_TENANT_GROUP_TENANTS, tenantGroupId]);
                queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
                history.push('/tenants');
            },
            {
                content: t('tenantGroupDeletedMsg', { tenantGroupName }),
                cancelText: t('Return to Manage Tenants'),
                status: 'error'
            }
        );
    };

    // handle 400 API error messages and other generic error messages
    const handleAPIError = (err: PostTenantsToGroupAPIError) => {
        addAlert({
            id: 'remove-tenants-from-tenantGroup-error',
            content: tenantGroupEntryKey('deleteTenantsAPIError', {
                errMsg: err.data?.error?.message ?? err.data?.message
            }),
            status: 'danger'
        });
        queryClient.invalidateQueries([RQ_TENANT_GROUP_TENANTS, tenantGroupId]);
    };

    return useMutation(
        [RQ_DELETE_TENANTS_FROM_TENANT_GROUP],
        (payload: DeleteTenantsFromGroupPayload) => deleteTenantsFromGroup(Number(tenantGroupId), payload),
        {
            onSuccess: (_data, variable) => {
                handleOnSuccess(variable);
            },
            onError: (err: DeleteTenantsFromGroupAPIError) => {
                switch (err.data.statusCode) {
                    case 409:
                        handleTenantGroupDeleted();
                        break;
                    case 404:
                        handleAlreadyDeleted();
                        break;
                    default:
                        handleAPIError(err);
                        break;
                }
            }
        }
    );
};

export default useRemoveTenantsFromTenantGroup;
